<script lang="ts">
  import { onMount, tick } from "svelte";
  import Progressbar from "./progressbar.svelte";
  import MessageModal from "./messageModal.svelte";
  import { opportunityOptions } from "../lib/opportunityOptionsStore";
  import { triggerOpportunityDataRefresh } from "../lib/syncDashboardRefresh";
  import { OpportunityOptionsResponse } from "../lib/types";
  import { AWS_APPROVED_READONLY_FIELDS } from "../lib/awsOpportunityUpdateRules";
  import { adminModuleCss } from "../lib/gruimModuleTheme";

  const DEFAULT_URL = process.env.SVELTE_APP_REMOTE_URL;

  let el1: HTMLDivElement;
  let el2: HTMLDivElement;
  let el3: HTMLDivElement;
  let el4: HTMLDivElement;

  let total = 0;
  let completed = 0;
  let showProgressBar = false;

  let showSyncModal = false;
  let syncMessage: string = "";
  let syncDetails: Record<string, unknown> | null = null;

  const closeModal = (): void => {
    showSyncModal = false;
    syncMessage = "";
    syncDetails = null;
  };

  let showSolutions = false;
  let showDelivery = false;
  let showSales = false;
  let toggling = false;

  let opts: OpportunityOptionsResponse | null = null;

  const toggleSolutions = async () => {
    toggling = true;
    showSolutions = !showSolutions;
    await tick();

    if (showSolutions && el2) {
      const { default: SolutionsOffered } = await import(
        "App/SolutionsOffered"
      );
      el2.innerHTML = "";
      new SolutionsOffered({
        target: el2,
        props: {
          css: adminModuleCss,
          translation: { name: "Solution Offered" },
          schema: {
            actions: { edit: { removable: true, "with-confirmation": true } },
            "field-properties": {
              "auto-generated-fields": ["id", "projectDetailId"],
              "field-order": ["name"],
            },
          },
        },
      });
    }
    toggling = false;
  };

  const toggleDelivery = async () => {
    toggling = true;
    showDelivery = !showDelivery;
    await tick();

    if (showDelivery && el3) {
      const { default: DeliveryModels } = await import("App/DeliveryModels");
      el3.innerHTML = "";
      new DeliveryModels({
        target: el3,
        props: {
          css: adminModuleCss,
          translation: { deliveryModel: "Delivery Model" },
          schema: {
            actions: { edit: { removable: true, "with-confirmation": true } },
            "field-properties": {
              "auto-generated-fields": ["id", "projectDetailId"],
              "field-order": ["deliveryModel"],
              "dropdown-fields": [
                {
                  name: "deliveryModel",
                  options: opts?.deliveryModel?.map((v) => ({ label: v, value: v })) ?? [],
                },
              ],
            },
          },
        },
      });
    }
    toggling = false;
  };

  const toggleSales = async () => {
    toggling = true;
    showSales = !showSales;
    await tick();

    if (showSales && el4) {
      const { default: ProjectSalesActivities } = await import(
        "App/ProjectSalesActivities"
      );
      el4.innerHTML = "";
      new ProjectSalesActivities({
        target: el4,
        props: {
          css: adminModuleCss,
          translation: { activity: "Sales Activity" },
          schema: {
            actions: { edit: { removable: true, "with-confirmation": true } },
            "field-properties": {
              "auto-generated-fields": ["id", "projectDetailId"],
              "field-order": ["activity"],
            },
          },
        },
      });
    }
    toggling = false;
  };

  const mountProjects = async () => {
    const { default: ProjectDetails } = await import("App/ProjectDetails");

    if (el1) {
      el1.innerHTML = "";
      new ProjectDetails({
        target: el1,
        props: {
          css: adminModuleCss,
          translation: {
            partnerProjectTitle: "Project Title",
            customerBusinessProblem: "Business Problem",
            useCase: "Use Case",
            solutionOffered: "Solution Offered",
            otherSolOffered: "Other Solution",
            apnPrograms: "APN Programs",
            estimatedAwsMonthlyReccuringRevenue: "Est. AWS MRR",
            additionalComments: "Comments",
            activity: "Sales Activity",
            competitiveTracking: "Competitive Tracking",
            deliveryModel: "Delivery Model",
            name: "Solution Offered",
            solutionsOffered: "Solutions Offered",
            deliveryModels: "Delivery Models",
            salesActivities: "Sales Activities",
            otherCompetitors: "Other Competitors",
          },
          schema: {
            actions: {
              edit: { removable: true, "with-confirmation": true },
            },
            "field-properties": {
              "hidden-fields": [
                "expectedCustomerSpendCurrencyCode",
                "expectedCustomerSpendFrequency",
                "expectedCustomerSpendTargetCompany",
              ],
              "auto-generated-fields": ["id"],
              "readonly-fields": AWS_APPROVED_READONLY_FIELDS.filter((field) =>
                ["partnerProjectTitle", "customerBusinessProblem"].includes(field)
              ),
              "field-order": [
                "partnerProjectTitle",
                "customerBusinessProblem",
                "useCase",
                "solutionsOffered",
                "otherSolOffered",
                "apnPrograms",
                "estimatedAwsMonthlyReccuringRevenue",
                "additionalComments",
              ],
              "relational-fields": [
                {
                  name: "salesActivities",
                  type: "DEFAULT",
                  editable: true,
                  columns: ["activity"],
                },
                {
                  name: "deliveryModels",
                  type: "DEFAULT",
                  editable: true,
                  columns: ["deliveryModel"],
                },
                {
                  name: "solutionsOffered",
                  type: "DEFAULT",
                  editable: true,
                  columns: ["name"],
                },
              ],
              "dropdown-fields": [
                {
                  name: "industryVertical",
                  options: opts.industryVertical.map((v) => ({ label: v, value: v })),
                },
                {
                  name: "competitiveTracking",
                  options: opts.competitorName.map((v) => ({ label: v, value: v })),
                },
                {
                  name: "apnPrograms",
                  options: opts.apnPrograms.map((v) => ({ label: v, value: v })),
                },
                {
                  name: "useCase",
                  options: opts.marketingUseCase.map((v) => ({ label: v, value: v })),
                },
              ],
            },
            "custom-bulk-actions": [
              {
                action: "Sync to AWS",
                bulkFn: async (ids: number[]) => {
                  showProgressBar = true;
                  total = ids.length;
                  completed = 0;

                  const results = [];
                  for (const projectId of ids) {
                    const oppRes = await fetch(
                      `${DEFAULT_URL}/api/opportunities?filter={"where":{"projectId":${projectId}}}`
                    );
                    const opportunities = await oppRes.json();

                    if (!opportunities.length) {
                      results.push({
                        success: false,
                        message: `No opportunities for project ${projectId}`,
                      });
                      completed++;
                      continue;
                    }

                    for (const opp of opportunities) {
                      const res = await fetch(`${DEFAULT_URL}/api/sync/opportunity/${opp.id}`, {
                        method: "POST",
                      });
                      results.push(await res.json());
                    }

                    completed++;
                  }

                  showProgressBar = false;
                  if (results.some((result) => result?.success !== false)) {
                    triggerOpportunityDataRefresh();
                  }
                  return results;
                },
                singleFn: async (row: { id: number }) => {
                  const oppRes = await fetch(
                    `${DEFAULT_URL}/api/opportunities?filter={"where":{"projectId":${row.id}}}`
                  );
                  const opportunities = await oppRes.json();

                  if (!opportunities.length) {
                    syncMessage = "No opportunities for this project";
                    syncDetails = {};
                    showSyncModal = true;
                    return { success: false, message: syncMessage };
                  }

                  const results = [];
                  for (const opp of opportunities) {
                    const res = await fetch(`${DEFAULT_URL}/api/sync/opportunity/${opp.id}`, {
                      method: "POST",
                    });
                    const data = await res.json();
                    results.push(data);
                  }

                  syncMessage = "Project sync completed.";
                  syncDetails = results;
                  showSyncModal = true;
                  if (results.some((result) => result?.success !== false)) {
                    triggerOpportunityDataRefresh();
                  }

                  return { success: true, synced: results };
                },
              },
            ],
          },
        },
      });
    }
  };

  onMount(async () => {
    opts = await opportunityOptions.fetchOptions();
    mountProjects();
  });
</script>

{#if showProgressBar}
  <Progressbar {completed} {total} />
{/if}

<div bind:this={el1} />

<div class="space-x-2 mt-4">
  <button
    on:click={toggleSolutions}
    class="bg-blue-600 text-white px-4 py-2 rounded disabled:opacity-50"
    disabled={toggling}
  >
    {toggling ? "Loading..." : showSolutions ? "Hide Solutions" : "Show Solutions"}
  </button>
  <button
    on:click={toggleDelivery}
    class="bg-blue-600 text-white px-4 py-2 rounded disabled:opacity-50"
    disabled={toggling}
  >
    {toggling ? "Loading..." : showDelivery ? "Hide Delivery Models" : "Show Delivery Models"}
  </button>
  <button
    on:click={toggleSales}
    class="bg-blue-600 text-white px-4 py-2 rounded disabled:opacity-50"
    disabled={toggling}
  >
    {toggling ? "Loading..." : showSales ? "Hide Sales Activities" : "Show Sales Activities"}
  </button>
</div>

{#if showSolutions}
  <div bind:this={el2} />
{/if}
{#if showDelivery}
  <div bind:this={el3} />
{/if}
{#if showSales}
  <div bind:this={el4} />
{/if}

<MessageModal
  show={showSyncModal}
  title="Sync Result"
  message={syncMessage}
  details={syncDetails}
  onClose={closeModal}
/>
